import React from "react";
import { Trash } from "lucide-react";
import { InvoiceItemData, TaxRate } from "../../../../../types/invoices";
import { Input } from "../../../../ui/input";
import { EDSButton } from "../../../../eds/EDSButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";
import {
  calculateItemGross,
  calculateItemNet,
  calculateItemTax,
  formatDecimal,
  getFormattedTaxRates,
  getRateValue
} from "./invoiceUtils";

interface InvoiceItemRowProps {
  item: InvoiceItemData;
  index: number;
  taxRates: TaxRate[];
  currencySymbol: string;
  isEditing: boolean;
  onUpdateItem: (index: number, updates: Partial<InvoiceItemData>) => void;
  onRemoveItem: (index: number) => void;
}

const InvoiceItemRow = ({
  item,
  index,
  taxRates,
  currencySymbol,
  isEditing,
  onUpdateItem,
  onRemoveItem
}: InvoiceItemRowProps): React.JSX.Element => {
  const net = calculateItemNet(item);
  const tax = calculateItemTax(item);
  const gross = calculateItemGross(item);
  const isTaxInclusive = item.tax_inclusive !== false;
  const isLocked = item.locked_tax === true;
  const isHeading = item.type === "heading";
  const isFooter = item.type === "footer";
  const isStructuralRow = isHeading || isFooter;
  const hasMultipleRates =
    Array.isArray(item.tax_rates) && item.tax_rates.length > 1;
  const taxBreakdown = hasMultipleRates ? getFormattedTaxRates(item) : [];

  const handleDescriptionChange = (value: string): void => {
    onUpdateItem(index, { description: value });
  };

  const handleGrossChange = (value: string): void => {
    const numValue = parseFloat(value) || 0;
    onUpdateItem(index, { gross_amount: numValue });
  };

  const handleNetChange = (value: string): void => {
    const numValue = parseFloat(value) || 0;
    const rateValue = getRateValue(item.tax_rate);
    onUpdateItem(index, {
      net_amount: numValue,
      gross_amount: numValue * rateValue
    });
  };

  const handleTaxRateChange = (taxRateId: string): void => {
    if (taxRateId === "no-tax") {
      onUpdateItem(index, {
        taxable: false,
        tax_rate: undefined
      });
      return;
    }

    const selectedRate = taxRates.find(
      (rate) => (rate._id ?? rate.id ?? rate.label) === taxRateId
    );

    if (selectedRate) {
      onUpdateItem(index, {
        taxable: true,
        tax_rate: selectedRate,
        tax_inclusive: isTaxInclusive
      });
    }
  };

  const currentTaxRateId =
    item.taxable && item.tax_rate
      ? (item.tax_rate._id ??
        item.tax_rate.id ??
        item.tax_rate.label ??
        "no-tax")
      : "no-tax";

  const formatTaxRateDisplay = (rate: TaxRate): string => {
    const rateValue = rate.value ?? rate.rate ?? 0;
    const percentage = rateValue > 1 ? Math.round((rateValue - 1) * 100) : 0;
    return `${rate.label ?? rate.name} (${percentage}%)`;
  };

  // Heading row: bold text spanning full width, no editable inputs
  if (isHeading) {
    return (
      <tr
        className="InvoiceItems-Row border-b border-border"
        data-type="heading"
      >
        <td colSpan={isEditing ? 6 : 5} className="py-2 px-2 text-sm font-bold">
          {item.description ?? ""}
        </td>
      </tr>
    );
  }

  // Footer row: bold subtotal spanning description + amounts
  if (isFooter) {
    return (
      <tr
        className="InvoiceItems-Row border-b border-border"
        data-type="footer"
      >
        <td className="py-2 px-2 text-sm font-bold">
          {item.description ?? ""}
        </td>
        <td className="py-2 px-2 text-sm text-right font-bold">
          {currencySymbol}
          {formatDecimal(net)}
        </td>
        <td className="py-2 px-2 text-sm text-right font-bold">
          {currencySymbol}
          {formatDecimal(tax)}
        </td>
        <td className="py-2 px-2 text-sm text-right font-bold">
          {currencySymbol}
          {formatDecimal(gross)}
        </td>
        <td className="py-2 px-2 text-sm font-bold">
          {item.tax_rate?.label ?? ""}
        </td>
        {isEditing && <td className="py-2 px-2" />}
      </tr>
    );
  }

  if (!isEditing) {
    return (
      <tr className="InvoiceItems-Row border-b border-border">
        <td className="py-2 px-2 text-sm">{item.description ?? ""}</td>
        <td className="py-2 px-2 text-sm text-right">
          {currencySymbol}
          {formatDecimal(net)}
        </td>
        <td className="py-2 px-2 text-sm text-right">
          <span>
            {currencySymbol}
            {formatDecimal(tax)}
          </span>
          {hasMultipleRates && taxBreakdown.length > 0 && (
            <ul
              className="mt-1 space-y-0.5 list-none p-0"
              aria-label="Tax breakdown"
            >
              {taxBreakdown.map((entry) => (
                <li key={entry.label} className="text-xs text-muted-foreground">
                  {entry.label}: {currencySymbol}
                  {formatDecimal(entry.tax)}
                </li>
              ))}
            </ul>
          )}
        </td>
        <td className="py-2 px-2 text-sm text-right">
          {currencySymbol}
          {formatDecimal(gross)}
        </td>
        <td className="py-2 px-2 text-sm">
          {item.tax_rate?.label ?? "No Tax"}
        </td>
      </tr>
    );
  }

  return (
    <tr className="InvoiceItems-Row border-b border-border">
      <td className="py-2 px-2">
        <Input
          value={item.description ?? ""}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Description"
          className="text-sm"
          aria-label={`Item ${index + 1} description`}
        />
      </td>
      <td className="py-2 px-2">
        {isTaxInclusive ? (
          <span className="text-sm text-right block">
            {currencySymbol}
            {formatDecimal(net)}
          </span>
        ) : (
          <Input
            type="number"
            value={item.net_amount?.toString() ?? "0"}
            onChange={(e) => handleNetChange(e.target.value)}
            step="0.01"
            className="text-sm text-right w-24"
            aria-label={`Item ${index + 1} net amount`}
          />
        )}
      </td>
      <td className="py-2 px-2 text-right text-sm">
        <span>
          {currencySymbol}
          {formatDecimal(tax)}
        </span>
        {hasMultipleRates && taxBreakdown.length > 0 && (
          <ul
            className="mt-1 space-y-0.5 list-none p-0"
            aria-label="Tax breakdown"
          >
            {taxBreakdown.map((entry) => (
              <li key={entry.label} className="text-xs text-muted-foreground">
                {entry.label}: {currencySymbol}
                {formatDecimal(entry.tax)}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="py-2 px-2">
        {isTaxInclusive ? (
          <Input
            type="number"
            value={item.gross_amount?.toString() ?? "0"}
            onChange={(e) => handleGrossChange(e.target.value)}
            step="0.01"
            className="text-sm text-right w-24"
            aria-label={`Item ${index + 1} gross amount`}
          />
        ) : (
          <span className="text-sm text-right block">
            {currencySymbol}
            {formatDecimal(gross)}
          </span>
        )}
      </td>
      <td className="py-2 px-2">
        {isLocked ? (
          <span
            className="text-sm text-muted-foreground block py-1"
            aria-label={`Item ${index + 1} tax rate (locked)`}
            aria-disabled="true"
          >
            {item.tax_rate?.label ?? "No Tax"}
          </span>
        ) : (
          <Select value={currentTaxRateId} onValueChange={handleTaxRateChange}>
            <SelectTrigger
              className="text-sm h-9 w-32"
              aria-label={`Item ${index + 1} tax rate`}
            >
              <SelectValue placeholder="Select tax rate" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no-tax">No Tax</SelectItem>
              {taxRates.map((rate) => {
                const rateId = rate._id ?? rate.id ?? rate.label;
                return (
                  <SelectItem key={rateId} value={rateId ?? "unknown"}>
                    {formatTaxRateDisplay(rate)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="py-2 px-2 text-center">
        {!isStructuralRow && (
          <EDSButton
            variant="ghost"
            size="icon"
            onClick={() => onRemoveItem(index)}
            aria-label={`Remove item ${index + 1}`}
          >
            <Trash className="w-4 h-4" />
          </EDSButton>
        )}
      </td>
    </tr>
  );
};

export default InvoiceItemRow;
